import React from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Heart, Users, Crown, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { IMAGES } from "../lib/images";
import { Reveal, useSEO } from "../components/site/Reveal";

const TIERS = [
  {
    name: "Individual",
    icon: Users,
    tagline: "For the curious heart",
    benefits: [
      "Full access to member newsletters",
      "Invitations to online lectures & talk shows",
      "Early notice of concerts & exhibitions",
    ],
  },
  {
    name: "Family",
    icon: Heart,
    tagline: "Share the heritage",
    featured: true,
    benefits: [
      "All Individual benefits for the household",
      "Priority seating at cultural events",
      "Access to Yorùbá language learning circles",
      "Discounts on cooking demonstrations & workshops",
    ],
  },
  {
    name: "Patron",
    icon: Crown,
    tagline: "Champion the cause",
    benefits: [
      "All Family benefits",
      "Recognition as a founding supporter",
      "Invitations to centre openings worldwide",
      "Directly fund language & heritage programmes",
    ],
  },
];

export default function Membership() {
  useSEO(
    "Membership",
    "Become a member of ÀsÀNigHT Worldwide and help save Yorùbá language and cultural heritage from extinction. Support our programmes and join a global community."
  );

  const [form, setForm] = React.useState({ name: "", email: "", tier: "Family", country: "", message: "" });
  const [loading, setLoading] = React.useState(false);
  const [banner, setBanner] = React.useState(IMAGES.drummers);
  React.useEffect(() => {
    api.get("/site-settings").then((r) => { if (r.data?.banner_membership) setBanner(r.data.banner_membership); }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/membership", form);
      toast.success(data.message || "Welcome to the family!");
      setForm({ name: "", email: "", tier: "Family", country: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => document.getElementById("join-form")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div>
      {/* HERO */}
      <header className="relative min-h-[58vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img src={banner} alt="Yorùbá cultural community" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/70 to-night/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-16 w-full">
          <p className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-5">Membership</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-5xl lg:text-6xl text-cream leading-tight text-shadow-hero max-w-3xl"
          >
            Help Save a Living Heritage
          </motion.h1>
        </div>
      </header>

      {/* INTRO */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <Reveal>
          <p className="text-2xl md:text-3xl font-serif text-forest leading-relaxed mb-8">
            Won't you rather join us to save the Yorùbá language and cultural heritage from
            extinction?
          </p>
          <p className="text-ink-muted text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Become a member of ÀsÀNigHT Worldwide and help support our multifarious programmes —
            from language learning and lectures to concerts, exhibitions, and the cultural
            centres we are building around the world.
          </p>
          <button
            onClick={scrollToForm}
            data-testid="membership-hero-cta"
            className="inline-flex items-center gap-2 bg-gold text-night font-semibold px-8 py-4 rounded-full hover:bg-gold-light transition-colors"
          >
            <Sparkles className="w-4 h-4" /> Become a Member
          </button>
        </Reveal>
      </section>

      {/* TIERS */}
      <section className="bg-sand border-y border-parchment">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <Reveal>
            <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4 text-center">Choose Your Path</p>
            <h2 className="font-serif text-4xl md:text-5xl text-ink mb-14 text-center">Ways to Belong</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {TIERS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <div
                  data-testid={`tier-${t.name.toLowerCase()}`}
                  className={`h-full rounded-2xl p-8 border transition-all duration-300 ${
                    t.featured
                      ? "bg-forest text-cream border-gold shadow-2xl shadow-forest/20 md:-translate-y-3"
                      : "bg-cream text-ink border-parchment hover:-translate-y-1 hover:border-gold"
                  }`}
                >
                  <span className={`grid place-items-center w-12 h-12 rounded-full mb-6 ${t.featured ? "bg-gold text-night" : "bg-forest text-cream"}`}>
                    <t.icon className="w-5 h-5" />
                  </span>
                  <h3 className="font-serif text-3xl mb-1">{t.name}</h3>
                  <p className={`text-sm mb-6 ${t.featured ? "text-gold-soft" : "text-gold"}`}>{t.tagline}</p>
                  <ul className="space-y-3">
                    {t.benefits.map((b) => (
                      <li key={b} className="flex gap-3 text-sm leading-relaxed">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.featured ? "text-gold-soft" : "text-gold"}`} />
                        <span className={t.featured ? "text-cream/85" : "text-ink-muted"}>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => {
                      setForm((f) => ({ ...f, tier: t.name }));
                      scrollToForm();
                    }}
                    data-testid={`tier-select-${t.name.toLowerCase()}`}
                    className={`mt-8 w-full py-3 rounded-full font-semibold transition-colors ${
                      t.featured ? "bg-gold text-night hover:bg-gold-light" : "bg-forest text-cream hover:bg-forest-light"
                    }`}
                  >
                    Join as {t.name}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* JOIN FORM */}
      <section id="join-form" className="scroll-mt-24 max-w-2xl mx-auto px-6 py-24">
        <Reveal>
          <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4 text-center">Join the Family</p>
          <h2 className="font-serif text-4xl md:text-5xl text-ink mb-3 text-center">Become a Member</h2>
          <p className="text-ink-muted text-center mb-10">
            Fill in your details and we'll welcome you into the ÀsÀNigHT Worldwide community.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="bg-cream border border-parchment rounded-2xl p-8 space-y-5 shadow-xl shadow-ink/5">
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Full Name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="membership-name-input"
                className={inputCls}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="membership-email-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Country / City</label>
                <input
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  data-testid="membership-country-input"
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Membership Tier</label>
              <select
                value={form.tier}
                onChange={(e) => setForm({ ...form, tier: e.target.value })}
                data-testid="membership-tier-select"
                className={inputCls + " bg-white"}
              >
                {TIERS.map((t) => (
                  <option key={t.name} value={t.name}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Why do you want to join? (optional)</label>
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="membership-message-input"
                className={inputCls}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="membership-submit-button"
              className="w-full bg-gold text-night font-semibold py-4 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60"
            >
              {loading ? "Submitting..." : "Join ÀsÀNigHT Worldwide"}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}

const inputCls =
  "w-full border border-parchment rounded-md px-4 py-3 outline-none focus:border-gold focus:ring-1 focus:ring-gold bg-white text-ink";
